import { FormBlockInstance } from '../api-types';

export const NUMBER_STEPPER_TYPE = 'number_stepper';
export const NUMBER_STEPPER_TYPE_VERSION = 1;

export interface NumberStepperConfig {
  min: number | null;
  max: number | null;
  step: number | null;
  /**
   * From `semantic.unit`, which is its only home. Shown beside the value and never stored with the
   * answer — the number means whatever the question says it means.
   */
  unit: string | null;
}

export interface NumberStepperAnswer {
  value: number;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * A step must be positive or it is not a step.
 *
 * pro-core refuses zero and negative steps at authoring, so a stored definition should never carry
 * one — but this reader is deliberately tolerant, and tolerance here has a sharp edge: a zero step
 * makes `stepBy` a no-op and divides by zero in the answered check, which would leave a REQUIRED
 * question permanently unanswerable with no way for the respondent to tell why. Falling back to "no
 * step constraint" degrades to a usable control instead of a dead one.
 */
const asPositiveNumber = (value: unknown): number | null => {
  const parsed = asNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

export const readNumberStepperConfig = (
  block: FormBlockInstance
): NumberStepperConfig => {
  const interaction = asRecord(asRecord(block.config).interaction);
  const unit = block.semantic?.unit;
  return {
    min: asNumber(interaction.min),
    max: asNumber(interaction.max),
    step: asPositiveNumber(interaction.step),
    unit: typeof unit === 'string' && unit.length > 0 ? unit : null
  };
};

export const readNumberStepperAnswer = (
  answer: unknown
): NumberStepperAnswer | null => {
  const value = asNumber(asRecord(answer).value);
  return value === null ? null : { value };
};

/**
 * How many decimals the step implies.
 *
 * Used to round after arithmetic. `0.1 + 0.2` is `0.30000000000000004` in JavaScript, and sending
 * that would be refused by a server that only stores four decimal places — so every computed value
 * is snapped back to the precision the step actually describes.
 */
const decimalsOf = (step: number): number => {
  const text = String(step);
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : Math.min(text.length - dot - 1, 4);
};

const round = (value: number, step: number | null): number => {
  if (step === null) {
    return value;
  }
  const factor = 10 ** decimalsOf(step);
  return Math.round(value * factor) / factor;
};

/**
 * The first legal value — what an untouched stepper becomes on its first tap.
 *
 * Not what it DISPLAYS while untouched. Showing this before anyone has acted would claim an answer
 * nobody gave, and on an optional question that is fabricated data rather than a harmless default.
 */
export const firstValueOf = (config: NumberStepperConfig): number =>
  config.min ?? 0;

/**
 * Move by one step, staying inside the range.
 *
 * Clamped rather than refused: a plus button that stops responding at the maximum is
 * indistinguishable from a broken one, so it becomes disabled instead (see the component) and this
 * function is the guarantee behind that.
 */
export const stepBy = (
  value: number | null,
  direction: 1 | -1,
  config: NumberStepperConfig
): number => {
  // Untouched: the first tap lands on the first legal value rather than one step above it, so a
  // 5..25 range stepping by 10 opens at 5 and not at 15.
  if (value === null) {
    return firstValueOf(config);
  }
  const step = config.step ?? 1;
  let next = round(value + direction * step, config.step);
  if (config.min !== null && next < config.min) {
    next = config.min;
  }
  if (config.max !== null && next > config.max) {
    next = config.max;
  }
  return next;
};

/**
 * Whether the answer is complete enough to move on.
 *
 * Mirrors the server: inside the range, and on a step measured FROM THE MINIMUM rather than from
 * zero — a 5..25 range stepping by 10 offers 5, 15 and 25, and testing divisibility by 10 would
 * refuse every one of them.
 */
export const isNumberStepperAnswered = (
  answer: unknown,
  config: NumberStepperConfig
): boolean => {
  const parsed = readNumberStepperAnswer(answer);
  if (!parsed) {
    return false;
  }
  const { value } = parsed;
  if (config.min !== null && value < config.min) {
    return false;
  }
  if (config.max !== null && value > config.max) {
    return false;
  }
  if (config.step === null) {
    return true;
  }
  const origin = config.min ?? 0;
  const steps = (value - origin) / config.step;
  // Tolerance because the check is over floating point: 0.3 - 0 divided by 0.1 is 2.9999999999999996.
  return Math.abs(steps - Math.round(steps)) < 1e-9;
};
